import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { OptiBaySessionVerificationTest } from '@/types/optibay';

const sb = supabase as any;

export function useSessionVerificationTests(sessionId: string | undefined) {
  return useQuery({
    queryKey: ['optibay-session-verifications', sessionId],
    queryFn: async (): Promise<OptiBaySessionVerificationTest[]> => {
      if (!sessionId) return [];
      const { data, error } = await sb
        .from('optibay_session_verification_tests')
        .select('*')
        .eq('session_id', sessionId)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return (data ?? []) as OptiBaySessionVerificationTest[];
    },
    enabled: !!sessionId,
  });
}

export interface AddVerificationTestParams {
  sessionId: string;
  sessionTestPathId?: string;
  testName: string;
  description?: string;
  expectedResult?: string;
  sortOrder?: number;
}

export function useAddVerificationTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: AddVerificationTestParams) => {
      const { data, error } = await sb
        .from('optibay_session_verification_tests')
        .insert({
          session_id: params.sessionId,
          session_test_path_id: params.sessionTestPathId || null,
          test_name: params.testName,
          description: params.description || null,
          expected_result: params.expectedResult || null,
          sort_order: params.sortOrder ?? 0,
        })
        .select()
        .single();
      if (error) throw error;
      return data as OptiBaySessionVerificationTest;
    },
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ['optibay-session-verifications', data.session_id] });
    },
  });
}

export function useUpdateVerificationTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, updates }: {
      id: string;
      updates: Partial<Pick<OptiBaySessionVerificationTest, 'actual_result' | 'passed' | 'performed_at'>>;
    }) => {
      const { data, error } = await sb
        .from('optibay_session_verification_tests')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as OptiBaySessionVerificationTest;
    },
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ['optibay-session-verifications', data.session_id] });
    },
  });
}

export function useRemoveVerificationTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, sessionId }: { id: string; sessionId: string }) => {
      const { error } = await sb
        .from('optibay_session_verification_tests')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return { sessionId };
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['optibay-session-verifications', result.sessionId] });
    },
  });
}
